import React, { useState, useMemo, useEffect, useCallback, useRef, useContext } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Home, ClipboardCheck, FileCheck, FileSearch, CreditCard, Search, FileText, Phone, Users, Settings, LogOut, ChevronLeft, ChevronDown, Bell, Shield, ShieldCheck, Menu, ClipboardList, Lock, CreditCard as CreditCardIcon, Wrench, Check, FilePlus, HelpCircle, DollarSign, UserRound, UserCheck, UserX, Landmark, MessageSquare } from 'lucide-react';
import { notifications as mockNotifications } from '../data/mockData';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { SessionTimeoutModal } from '../components/SessionTimeoutModal';
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import { startSignalR } from "../../src/services/signalrService";
import { userApi } from '../api/userApi';
import appConfig from '../config/appConfig';
import { eopApi } from '../api/eopApi';

import { LoadingSpinner, ErrorCard } from "../components/LoadingSpinner";

import { useToast } from '../components/Toast';

import { useConfirm } from '../components/ConfirmDialog';

import { isApiConfigured } from '../api/axiosClient';

import { NotificationContext } from "../contexts/NotificationContext";
import { CsvFileDownload } from "../components/CsvFileDownload";
import { PdfFileDownload } from "../components/PdfFileDownload";
import SenderoLogo from '../components/SenderoLogo';
import ChatAssistant from '../components/ChatAssistant';
import AppTour from '../components/AppTour';
import MaintenanceBanner from '../components/MaintenanceBanner';
import FeedbackWidget from '../components/FeedbackWidget';
import SocialProof from '../components/SocialProof';

import { usePermission } from '../hooks/usePermission';

import senLogo from "../assets/CentralHealth_SecondaryLogo_Horiz_FullColor_RGB.png";


/* -- navigation items --------------------------------------------------- */
const navItems = [
    { text: 'Home', icon: Home, route: '/' },
    { text: 'Eligibility & Benefits', icon: ClipboardCheck, route: '/eligibility' },
    //{
    //    text: 'Authorizations', icon: FileCheck, route: '/authorizations',
    //    children: [
    //        //{ text: 'New Request', icon: FilePlus, route: '/authorizations' },
    //        { text: 'Auth Inquiry', icon: FileSearch, route: '/authorizations/inquiry' },
    //    ],
    //},
    {
        text: 'Provider Roster', icon: UserRound, route: '/proster',
        providerAdminAndOwnUserOnly:true
    },
    {
        text: 'Claims & Payments', icon: CreditCard, route: '/claims',
        children: [
            { text: 'Claim Status', icon: CreditCard, route: '/claims' },
            { text: 'Claim Appeals', icon: ClipboardList, route: '/appeals' },
        ],
    },
    // { text: 'Invoices & Billing',  icon: Receipt,         route: '/invoices' },
    { text: 'EOP Search', icon: Search, route: '/eop', separator: true },
    {
        text: 'Forms & Resources', icon: FileText, route: '/forms',
        children: [
            { text: 'Forms & Downloads', icon: FileText, route: '/forms' },
            { text: 'EFT Enrollment', icon: Landmark, route: '/forms/eft-enrollment', providerAdminOnly: true },
            { text: 'ERA Enrollment', icon: FileText, route: '/forms/era-enrollment', providerAdminOnly: true },
        ],
    },
    { text: 'Contact & Resources', icon: Phone, route: '/contactres', separator: true },
    //{ text: 'User Management', icon: Users, route: '/admin/users' },
    {
        text: 'User Management', icon: Users, route: '/admin/users',
        providerAdminOnly:true
    },
    { text: 'Super Admin', icon: ShieldCheck, route: '/admin/system-users',
        //superAdminOnly: true,
        superAdminAndAdminOnly:true,
        children: [
            { text: 'System Users', icon: Users, route: '/admin/system-users' },
            { text: 'System Settings', icon: Settings, route: '/admin/system-settings' },
            { text: 'Feedback', icon: MessageSquare, route: '/admin/feedback' },
            { text: 'EFT Enrollments', icon: Landmark, route: '/forms/eft-enrollment-list' },
            { text: 'ERA Enrollments', icon: Landmark, route: '/forms/era-enrollment-list' },
        ],
    },
    { text: 'Profile', icon: Settings, route: '/profile' },
    { text: 'Help & Support', icon: HelpCircle, route: '/help' },
];

/* -- colour tokens ------------------------------------------------------ */
const COLOR = {
    primary: '#0D9488',
    dark: '#0F766E',
    deep: '#134E4A',
    accent: '#2DD4BF',
};

const SIDEBAR_W_OPEN = 260;
const SIDEBAR_W_CLOSED = 60;
const TOPBAR_H = 64;
const TOPBAR_H_MOBILE = 56;

/* -- breakpoints -------------------------------------------------------- */
const BP_MOBILE = 768;
const BP_TABLET = 1024;

/* -- component ---------------------------------------------------------- */
export const MainLayout = () => {
    const { allNotificationData, refreshNotifications} =
        useContext(NotificationContext);

    const toast = useToast();
    const confirm = useConfirm();   
    const { can, canAny, canAll } = usePermission();
    const [expanded, setExpanded] = useState(true);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [tabletHover, setTabletHover] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    //const [notifs, setNotifs] = useState(mockNotifications);
    const [notifs, setNotifs] = useState([]);
    const [notLoading, setNotLoading] = useState(false);
    //const [allNotificationData, setAllNotificationData] = useState([]);
    const [expandedMenus, setExpandedMenus] = useState({});
    const [downloadingEop, setDownloadingEop] = useState(null);
    // Feedback "collect" flag (super-admin controlled) — gates the footer link.
    const [feedbackCollect, setFeedbackCollect] = useState(() => appConfig.getFeedbackConfig().collect);
    useEffect(() => appConfig.subscribe(() => setFeedbackCollect(appConfig.getFeedbackConfig().collect)), []);

    /* -- responsive state ------------------------------------------------- */
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < BP_MOBILE);
    const [isTablet, setIsTablet] = useState(
        () => window.innerWidth >= BP_MOBILE && window.innerWidth < BP_TABLET,
    );

    

    const { user, logout, refreshAccessToken, isImpersonating, originalUser, endImpersonation, signalRLogout } = useAuth();

    const [accessPermissions, setAccessPermissions] = useState({});

    useEffect(() => {
        setAccessPermissions({

            canApproveProviderAdmin: can(
                "superAdmin",
                "systemUsers",
                "approveProviderAdmin",
            ),
            canRejectProviderAdmin: can(
                "superAdmin",
                "systemUsers",
                "rejectProviderAdmin",
            )

        });
    }, [user]);

    // Session timeout - auto-logout on inactivity
    const sessionTimeout = useSessionTimeout({
        enabled: !!user,
        onLogout: () => {
            logout();
            navigate('/login', { state: { reason: 'timeout' } });
        },
    });
    const navigate = useNavigate();
    const location = useLocation();

    /* -- derived helpers -------------------------------------------------- */
    const topBarHeight = isMobile ? TOPBAR_H_MOBILE : TOPBAR_H;

    const sidebarWidth = useMemo(() => {
        if (isMobile) return SIDEBAR_W_OPEN;
        if (isTablet) return tabletHover ? SIDEBAR_W_OPEN : SIDEBAR_W_CLOSED;
        return expanded ? SIDEBAR_W_OPEN : SIDEBAR_W_CLOSED;
    }, [isMobile, isTablet, tabletHover, expanded]);

    const showLabels = useMemo(() => {
        if (isMobile) return true;
        if (isTablet) return tabletHover;
        return expanded;
    }, [isMobile, isTablet, tabletHover, expanded]);

    const selectedRoute = useMemo(() => {
        const path = location.pathname;
        // Check flat items and children
        for (const item of navItems) {
            if (item.children) {
                const child = item.children.find(c => c.route === path);
                if (child) return child.route;
            }
            if (item.route === path) return item.route;
        }
        // Prefix match
        const allRoutes = navItems.flatMap(i => i.children ? i.children.map(c => c) : [i]);
        const prefix = allRoutes.filter(i => i.route !== '/').find(i => path.startsWith(i.route));
        return prefix ? prefix.route : '/';
    }, [location.pathname]);

    // Auto-expand parent menus when a child is active
    useEffect(() => {
        const path = location.pathname;
        navItems.forEach(item => {
            if (item.children) {
                const hasActiveChild = item.children.some(c => path.startsWith(c.route));
                if (hasActiveChild) {
                    setExpandedMenus(prev => ({ ...prev, [item.text]: true }));
                }
            }
        });
    }, [location.pathname]);

    


    const [userInitials, setUserInitials] = useState('U');

    useEffect(() => {
        setUserInitials(
            user
                ? `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`
                : 'U'
        );
    }, [user]);


    useEffect(() => {
        const handleResize = () => {
            const w = window.innerWidth;
            setIsMobile(w < BP_MOBILE);
            setIsTablet(w >= BP_MOBILE && w < BP_TABLET);
            if (w >= BP_MOBILE) setSidebarOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleNavClick = useCallback((route) => {
        navigate(route);
        if (isMobile) setSidebarOpen(false);
    }, [navigate, isMobile]);

    /* -- styles ----------------------------------------------------------- */
    const sidebarStyle = {
        width: sidebarWidth,
        minWidth: sidebarWidth,
        height: '100vh',
        background: `linear-gradient(180deg, ${COLOR.dark} 0%, ${COLOR.deep} 100%)`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.25s cubic-bezier(.4,0,.2,1), min-width 0.25s cubic-bezier(.4,0,.2,1), transform 0.25s cubic-bezier(.4,0,.2,1)',
        overflow: 'hidden',
        zIndex: 200,
        ...(isMobile
            ? {
                position: 'fixed',
                top: 0,
                left: 0,
                transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
            }
            : isTablet
                ? {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                }
                : {
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'flex-start',
                }),
    };

    const brandStyle = {
        height: topBarHeight,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: showLabels ? '0 20px' : '0 12px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        cursor: 'pointer',
        flexShrink: 0,
        justifyContent: showLabels ? 'flex-start' : 'center',
    };

    const itemBase = {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: showLabels ? '11px 18px' : '11px 0',
        margin: showLabels ? '2px 12px' : '2px 8px',
        borderRadius: 12,
        cursor: 'pointer',
        transition: 'all 0.18s ease',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        justifyContent: showLabels ? 'flex-start' : 'center',
        position: 'relative',
    };

    const getItemStyle = (item) => {
        const isActive = selectedRoute === item.route;
        const isHovered = hoveredItem === item.route;

        if (isActive) {
            return {
                ...itemBase,
                background: 'rgba(255,255,255,0.95)',
                color: COLOR.deep,
                fontWeight: 700,
                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            };
        }
        if (isHovered) {
            return {
                ...itemBase,
                background: 'rgba(255,255,255,0.12)',
                color: '#ffffff',
            };
        }
        return {
            ...itemBase,
            background: 'transparent',
            color: 'rgba(255,255,255,0.7)',
        };
    };

    const topBarStyle = {
        height: topBarHeight,
        background: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '0 12px' : '0 24px',
        gap: isMobile ? 8 : 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        flexShrink: 0,
        zIndex: 100,
    };

    const mainContentStyle = {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        background: '#f8fafb',
        padding: isMobile ? '20px 16px' : '28px 32px',
    };

    const backdropStyle = {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 199,
        transition: 'opacity 0.25s ease',
        opacity: sidebarOpen ? 1 : 0,
        pointerEvents: sidebarOpen ? 'auto' : 'none',
    };

    const hamburgerStyle = {
        width: 40,
        height: 40,
        borderRadius: 10,
        border: 'none',
        background: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: 20,
        color: '#374151',
        flexShrink: 0,
        transition: 'background 0.2s',
    };

    const footerStyle = {
        background: '#0F172A',
        padding: isMobile ? '20px 16px' : '16px 32px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile ? 12 : 24,
        fontSize: 13,
        color: 'rgba(255,255,255,0.5)',
        flexShrink: 0,
    };

    const footerLinkStyle = {
        color: 'rgba(255,255,255,0.6)',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'color 0.2s',
    };

    
    const [notifications, setNotifications] = useState([]);
  

    const connectionRef = useRef(null);

    useEffect(() => {
        const token = localStorage.getItem("sendero_token");

        refreshNotifications();
        
      
        if (!token) return;

        // Prevent duplicate connection
        if (connectionRef.current) return;

        const connection =  startSignalR(token, async(data) => {            

            if (data.type === "UserLogout") {
                await signalRLogout();
                
            }

            if (data.type === "UserPermissionUpdate") {
                const success = await refreshAccessToken(localStorage.getItem('sendero_refresh_token'), isImpersonating);        
                if (success) {
                    console.log("New token get successfully");
                }

            }

            if (data.type === "AppSettingsUpdate") {
                
                appConfig.loadServerSettings();

            }
            await refreshNotifications();
        });

        connectionRef.current = connection;

        return () => {
          //  connectionRef.current?.stop();
            connectionRef.current = null;
        };

        

    }, [user?.userId]);

    const GetAppSettings = async() => {

    }



    const styles = {
        overlay: {
            position: "fixed",
            inset: 0, // replaces top/left/width/height
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
        },
        gridOverlay: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
        },
    };


    useEffect(() => {
        
        
        if (allNotificationData.length > 0) {

            setNotLoading(true);

            const uniqueNotifications = Array.from(
                new Map(
                    allNotificationData.map(item => [
                        `${item.userId}|${item.title}|${item.message}|${item.type}|${item.referenceId}|${item.fileUrl}`,
                        item
                    ])
                ).values()
            );

            //const notifications = uniqueNotifications
            //    .filter(item => !item.fileUrl?.toLowerCase().includes("error")
            //        && !item.isRead
            //    )
            //    .map(item => ({
            //        id: item.id,
            //        //title: `EOP for Check : ${item.referenceId}`,
            //        title: item?.type?.trim().toLowerCase() === 'claimeop' ? `EOP for Claim : ${item.referenceId}` : `EOP for Check : ${item.referenceId}`,
            //        checkNo: item.message?.split("|")[1]?.trim().split(/\s+/)[1] || "",
            //        message: `is ready for `,
            //        downloadUrl: item.fileUrl,
            //        type: 'EOP',
            //        read: item.isRead,
            //        date: item.createdAt,
            //    }));

            const getFieldValue = (text, fieldName) =>
                text.match(new RegExp(`${fieldName}\\s*:\\s*(.+)`, "i"))?.[1]?.trim() ?? "";

            const notifications = uniqueNotifications
                .filter(item =>
                    !item.fileUrl?.toLowerCase().includes("error") &&
                    !item.isRead
                )
                .map(item => {
                    const isProviderAdminApproval =
                        item?.type?.trim().toLowerCase() === "provideradminapproval";

                    return {
                        id: item.id,
                        title: isProviderAdminApproval
                            ? item.title
                            : item?.type?.trim().toLowerCase() === "claimeop"
                                ? `EOP for Claim : ${item.referenceId}`
                                : `EOP for Check : ${item.referenceId}`,
                        checkNo: isProviderAdminApproval
                            ? ""
                            : item.message?.split("|")[1]?.trim().split(/\s+/)[1] || "",
                        others: isProviderAdminApproval
                            ? {
                                providerName: getFieldValue(item.message, "Provider\\s+Name"),
                                orgName: getFieldValue(item.message, "Organization"),
                                taxId: getFieldValue(item.message, "TaxId"),
                                registrationDate: getFieldValue(item.message, "Registration\\s+Date")
                            }
                            : null,
                        message: isProviderAdminApproval
                            ? (
                                <>
                                    {item.message.split("\n").map((line, index) => {
                                        const parts = line.split(":");

                                        return (
                                            <div key={index}>
                                                {parts.length > 1 ? (
                                                    <>
                                                        <strong>{parts[0]}</strong>
                                                        {" : "}
                                                        {parts.slice(1).join(":").trim()}
                                                    </>
                                                ) : (
                                                    line
                                                )}
                                            </div>
                                        );
                                    })}
                                </>
                            )
                            : "is ready for ",
                        downloadUrl: isProviderAdminApproval
                            ? ""
                            : item.fileUrl,
                        type: isProviderAdminApproval
                            ? "ProviderAdminApproval"
                            : "EOP",
                        read: item.isRead,
                        date: item.createdAt,
                        createdBy: item.createdBy,
                        //isApproved: isProviderAdminApproval
                        //    ? item.isApproved?.toLowerCase() === "true"
                        //    : "",
                        isApproved: isProviderAdminApproval
                            ? (item.isApproved?.trim().toLowerCase() || "")
                            : "",
                        approvedBy: isProviderAdminApproval
                            ? item.approvedBy
                            : "",
                        rejectedBy: isProviderAdminApproval
                            ? item.rejectedBy
                            : "",
                    };
                });
            setNotifs(notifications);
            setNotLoading(false);

        }
        else {
            setNotLoading(false);
            setNotifs([]);
        }
    }, [allNotificationData]);

    const handleFileDownload = async (fileName, checkNo, id) => {

        // alert("FILE NAME : " + fileName + "|" + "id : " + id + "checkNo : " + checkNo);
        //return;

        const extension = fileName.split('.').pop();
        const match = fileName.match(/_(\d+)_(\d+)\.[a-zA-Z0-9]+$/);

        let billProvider;
        let chkNumber;
        let claimNumber;
        let response;

        setDownloadingEop(id);

        if (fileName.includes("EOPClaimReport")) {

            if (match) {
                billProvider = match[1];
                claimNumber = match[2];

                response = await eopApi.downloadClaimEOP({
                    billingProvider: billProvider,
                    checkNumber: checkNo,
                    claimNumber: claimNumber,
                    fileName: fileName
                });

            }
            else {
                toast.error('Oops! Something went wrong ', { title: 'Notification' });

            }
        }
        else if (fileName.includes("EOPDetailReport")) {
            if (match) {
                billProvider = match[1];
                chkNumber = match[2];

                response = await eopApi.downloadEOP({
                    billingProvider: billProvider,
                    checkNumber: chkNumber,
                    fileName: fileName
                });

            }
            else {
                toast.error('Oops! Something went wrong ', { title: 'Notification' });
            }
        }
        else {
            toast.error('Oops! Something went wrong ', { title: 'Notification' });
        }

        if (extension?.trim().toLowerCase() === "pdf") {
            //await downloadPdfFileCard(response, fileName);
            await PdfFileDownload(response, fileName);
        }

        if (extension?.trim().toLowerCase() === "csv") {
            //await downloadCsvFileCard(response, fileName);


            const headers = [
                "Claim_Number",
                "Claim_Seq",
                "Account_No",
                "Procedure_Code",
                "Services_Provided",
                "Member_Id",
                "Member_Name",
                "Modifier",
                "Service_Date",
                "Service_Thru",
                "Svc_Count",
                "Billed_Price",
                "Plan_Price",
                "Discount_Amt",
                "Paid_Amt",
                "Copay_Amt",
                "Not_Covered_Amt",
                "Deductible_Amt",
                "Co_Ins_Amt",
                "COB_Amt",
                "Ex_Code",
                "Ex_Description",
                "Your_Rep",
                "Services_Desc",
                "TableName"
            ];
            await CsvFileDownload(response, fileName, headers);
        }

        setDownloadingEop(null);


    };

    //const handleFileDownload = async(fileName,id) => {
        
    //    const extension = fileName.split('.').pop();
    //    const match = fileName.match(/_(\d+)_(\d+)\.[a-zA-Z0-9]+$/);

    //    let billProvider;
    //    let chkNumber;
    //    if (match) {
    //        billProvider = match[1];
    //        chkNumber = match[2];

    //    }

    //    //alert(billProvider + "|" + chkNumber);
    //    //return;

    //    setDownloadingEop(id);
    //    const response = await eopApi.downloadEOP({
    //        billingProvider: billProvider,
    //        checkNumber: chkNumber,
    //        fileName: fileName
    //    });


    //    if (extension?.trim().toLowerCase() === "pdf") {
            
    //        await PdfFileDownload(response, fileName);
    //    }

    //    if (extension?.trim().toLowerCase() === "csv") {
            

    //        const headers = [
    //            "Claim_Number",
    //            "Claim_Seq",
    //            "Account_No",
    //            "Procedure_Code",
    //            "Services_Provided",
    //            "Member_Id",
    //            "Member_Name",
    //            "Modifier",
    //            "Service_Date",
    //            "Service_Thru",
    //            "Svc_Count",
    //            "Billed_Price",
    //            "Plan_Price",
    //            "Discount_Amt",
    //            "Paid_Amt",
    //            "Copay_Amt",
    //            "Not_Covered_Amt",
    //            "Deductible_Amt",
    //            "Co_Ins_Amt",
    //            "COB_Amt",
    //            "Ex_Code",
    //            "Ex_Description",
    //            "Your_Rep",
    //            "Services_Desc",
    //            "TableName"
    //        ];
    //        await CsvFileDownload(response, fileName,headers);
    //    }

    //    setDownloadingEop(null);
        
    //};

    //const downloadCsvFileCard = async (response, fileName) => {

    //    const headers = [
    //        "Claim_Number",
    //        "Claim_Seq",
    //        "Account_No",
    //        "Procedure_Code",
    //        "Services_Provided",
    //        "Member_Id",
    //        "Member_Name",
    //        "Modifier",
    //        "Service_Date",
    //        "Service_Thru",
    //        "Svc_Count",
    //        "Billed_Price",
    //        "Plan_Price",
    //        "Discount_Amt",
    //        "Paid_Amt",
    //        "Copay_Amt",
    //        "Not_Covered_Amt",
    //        "Deductible_Amt",
    //        "Co_Ins_Amt",
    //        "COB_Amt",
    //        "Ex_Code",
    //        "Ex_Description",
    //        "Your_Rep",
    //        "Services_Desc",
    //        "TableName"
    //    ];

    //    const csvText = await response.data.text();

    //    const csvContent =
    //        headers.join(",") + "\n" + csvText;

    //    const blob = new Blob([csvContent], {
    //        type: "text/csv;charset=utf-8;"
    //    });

    //    const url = URL.createObjectURL(blob);

    //    const a = document.createElement("a");
    //    a.href = url;
    //    a.download = fileName;

    //    document.body.appendChild(a);
    //    a.click();

    //    document.body.removeChild(a);
    //    URL.revokeObjectURL(url);
    //};

    //const downloadCsvFileCard = (response, fileName) => {

        
    //    const blob = new Blob([response.data], {
    //        type: "text/csv;charset=utf-8;"
    //    });

    //    const url = URL.createObjectURL(blob);

    //    const a = document.createElement("a");
    //    a.href = url;
    //    a.download = fileName;
    //    document.body.appendChild(a);
    //    a.click();

    //    document.body.removeChild(a);
    //    URL.revokeObjectURL(url);
    //};

    //const downloadPdfFileCard = (response, fileName) => {


    //    const blob = response.data;

    //    const downloadUrl = window.URL.createObjectURL(blob);

    //    const a = document.createElement("a");
    //    a.href = downloadUrl;
    //    a.download = fileName;
    //    document.body.appendChild(a);
    //    a.click();

    //    a.remove();
    //    window.URL.revokeObjectURL(downloadUrl);
    //};

    const markNotificationAsRead = async (notfIds) => {
        //alert(notfIds);
        setNotLoading(true);
        if (isApiConfigured) {

            try {
                const response = await userApi.markUserNotificationAsRead({
                    ids: notfIds, //array,                    
                    userId: user?.userId
                });

                await refreshNotifications();


            } catch (err) {

                console.log("Error to mark notification as read");
                setNotLoading(false);

            } finally {
                setNotLoading(false);
            }
        }
    };

    const markAllAsRead = async () => {
        const ids = notifs
            .filter(n => !n.read)
            .map(n => n.id);

        await markNotificationAsRead(ids);

        setNotifs(notifs.map(n => ({ ...n, read: true })));
    };


    const searchItems = [
        { text: "Claims and details", path: "/claims" },
        { text: "My Appeals", path: "/appeals" },
        { text: "User Dashboard", path: "/" },
        //{ text: "Authorizations", path: "/authorizations" },
        { text: "Search Members", path: "/eligibility" },
        { text: "User Profile", path: "/profile" },
        { text: "Forms and Downloads", path: "/forms" },
        { text: "EFT Enrollment", path: "/forms/eft-enrollment" },
        { text: "ERA Enrollment", path: "/forms/era-enrollment" },
        { text: "EOP", path: "/eop" },
        { text: "User Notifications", path: "/notifications" },
        { text: "Help and Support", path: "/help" },
    ];


    const approveOrRejectUser = async (userId,apprStatus,others) => {

        

        const nextStatus = apprStatus;     
        const next = apprStatus;     
        
        const providerName = others.providerName;
        const orgName = others.orgName;
        const role="Provider Admin";

        const ok = await confirm({
            title: `${next ? "Approve" : "Reject"} ${providerName}?`,
            message: `${orgName} · ${role}. ${next === true
                ? `${providerName} will gain access to the portal.`
                : `${providerName} will be rejected.`
                }`,
            confirmLabel: next === true ? 'Approve' : 'Reject',
            variant: next === true ? 'approve' : 'reject',
        });

        
        if (!ok) return;
        const displayName = `${providerName}`;

        let rejectReason = "";

        if (!nextStatus) {
            rejectReason = ok.reason
        }

        if (isApiConfigured()) {
            
            userApi.approveUser({
                userId: userId,
                currentapproveStatus: nextStatus,
                rejectReason: rejectReason
            })
                .then(async (res) => {

                    if (res.success) {
                        toast.success(
                            `${displayName} is now ${next ? "Approved" : "Rejected"}`,
                            {
                                title: next ? "User approved" : "User rejected"
                            }
                        );
                    }
                    else {
                        toast.warning(
                            res.message,
                            {
                                title: "User Approved"
                            }
                        );
                        
                    }

                    await refreshNotifications();
                    
                    
                })
                .catch((err) => {

                    console.error(err);
                    
                    toast.error(err.message || 'Failed to Process', {
                        title: 'Error',
                    });
                });
            //.catch((err) => toast.error(err.message || 'Failed to toggle status', { title: 'Status change failed' }));
        } else {
            setUsers(users.map(x => x.id === userId ? { ...x, isApproved: next } : x));
            toast.success(
                `${displayName} is now ${next}.`,
                { title: next === 'Active' ? 'User Approved' : 'User Disapproved' }
            );
        }
    };

    const canAccessNavItem = (item) => {
        // Super Admin only
        if (item.superAdminOnly) {
            return user?.userRole === 'Super Admin';
        }

        //Super Admin and Admin only
        if (item.superAdminAndAdminOnly) {
            return ['Super Admin', 'Admin'].includes(user?.userRole);
        }

        // Provider Admin only
        if (item.providerAdminOnly) {
            return user?.userRole === 'Provider Admin';
        }

        // Provider Admin and own user only
        if (item.providerAdminAndOwnUserOnly) {
            return user?.userRole !== 'Super Admin' && user?.userRole !== 'Admin';
        }

        return true;
    };

    const filteredNavItems = navItems
        .filter(canAccessNavItem)
        .map(item => ({
            ...item,
            children: item.children
                ? item.children.filter(canAccessNavItem)
                : undefined,
        }));

    //console.log("--------------------------------------------------------");
    //console.log("filteredNavItems: " + JSON.stringify(filteredNavItems));
    //console.log("--------------------------------------------------------");


    

    /* -- render ----------------------------------------------------------- */
    return (
        <>
        {/* -- MAINTENANCE BANNER (global; super-admin controlled) -----------
            Above the sidebar + content row so it spans the FULL window width. */}
        <MaintenanceBanner />

        <div style={{ display: 'flex', minHeight: '100vh' }}>
            {/* -- MOBILE BACKDROP --------------------------------------------- */}
            {isMobile && (
                <div
                    style={backdropStyle}
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* -- TABLET HOVER BACKDROP --------------------------------------- */}
            {isTablet && tabletHover && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.2)',
                        zIndex: 199,
                        transition: 'opacity 0.2s ease',
                    }}
                    onMouseEnter={() => setTabletHover(false)}
                />
            )}

            {/* -- SIDEBAR ----------------------------------------------------- */}
            <aside
                style={sidebarStyle}
                onMouseEnter={isTablet ? () => setTabletHover(true) : undefined}
                onMouseLeave={isTablet ? () => setTabletHover(false) : undefined}
            >
                {/* brand header */}
                {/*<div style={brandStyle} onClick={() => handleNavClick('/')}>*/}
                {/*    {showLabels ? (*/}
                {/*        <SenderoLogo mono="white" height={30} />*/}
                {/*    ) : (*/}
                {/*        <SenderoLogo variant="mark" mono="white" height={34} />*/}
                {/*    )}*/}
                {/*</div>*/}

                {/* brand header */}
                {/*<img src={senLogo} width="200" height="40"></img>*/}
                <div style={brandStyle} onClick={() => handleNavClick('/')}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: 'rgba(255,255,255,0.18)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Shield size={20} strokeWidth={2} />
                        <div style={{ width: 35, height: 35, borderRadius: 22, background: '#fff', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 20px rgba(0,0,0,0.1)', padding: 8 }}>
                            <SenderoLogo variant="mark" height={30} />
                        </div>

                        
                    </div>
                    {showLabels && (
                        <span style={{
                            fontSize: 20, fontWeight: 800, color: '#fff',
                            letterSpacing: -0.5, lineHeight: 1,
                        }}>
                            CENTRAL HEALTH
                        </span>
                    )}
                </div>


                {/* nav items */}
                <nav data-tour="sidebar-nav" style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                    {/*{navItems.filter(item => !item.superAdminOnly || user?.userRole === 'Super Admin').map((item) => (*/}
                   {filteredNavItems
                        .map((item) => (
                        <React.Fragment key={item.text}>
                            {item.separator && (
                                <div style={{
                                    height: 1, background: 'rgba(255,255,255,0.1)',
                                    margin: showLabels ? '10px 20px' : '10px 10px',
                                }} />
                            )}

                            {/* Item with children (submenu) */}
                            {item.children ? (
                                <div>
                                    {/* Parent item */}
                                    <div
                                        style={{
                                            ...itemBase,
                                            background: item.children.some(c => selectedRoute === c.route) ? 'rgba(255,255,255,0.08)' : hoveredItem === item.text ? 'rgba(255,255,255,0.12)' : 'transparent',
                                            color: item.children.some(c => selectedRoute === c.route) ? '#fff' : hoveredItem === item.text ? '#fff' : 'rgba(255,255,255,0.7)',
                                            fontWeight: item.children.some(c => selectedRoute === c.route) ? 600 : 'inherit',
                                        }}
                                        onClick={() => {
                                            if (showLabels) {
                                                setExpandedMenus(prev => ({ ...prev, [item.text]: !prev[item.text] }));
                                            } else {
                                                handleNavClick(item.children[0].route);
                                            }
                                        }}
                                        onMouseEnter={() => setHoveredItem(item.text)}
                                        onMouseLeave={() => setHoveredItem(null)}
                                        title={showLabels ? undefined : item.text}
                                    >
                                        <span style={{ lineHeight: 1, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                                            <item.icon size={18} strokeWidth={2} />
                                        </span>
                                        {showLabels && (
                                            <>
                                                <span style={{ fontSize: 14, fontWeight: 'inherit', flex: 1 }}>
                                                    {item.text}
                                                </span>
                                                <ChevronDown
                                                    size={14}
                                                    strokeWidth={2}
                                                    style={{
                                                        transition: 'transform 0.2s',
                                                        transform: expandedMenus[item.text] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        opacity: 0.6,
                                                    }}
                                                />
                                            </>
                                        )}
                                    </div>

                                    {/* Children */}
                                    {showLabels && expandedMenus[item.text] && (
                                        <div style={{ overflow: 'hidden' }}>
                                            {item.children.map(child => {
                                                const isChildActive = selectedRoute === child.route;
                                                const isChildHovered = hoveredItem === child.route;
                                                return (
                                                    <div
                                                        key={child.route}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 10,
                                                            padding: '9px 18px 9px 48px',
                                                            margin: '1px 12px',
                                                            borderRadius: 10,
                                                            cursor: 'pointer',
                                                            transition: 'all 0.15s ease',
                                                            background: isChildActive ? 'rgba(255,255,255,0.95)' : isChildHovered ? 'rgba(255,255,255,0.08)' : 'transparent',
                                                            color: isChildActive ? COLOR.deep : isChildHovered ? '#fff' : 'rgba(255,255,255,0.6)',
                                                            fontWeight: isChildActive ? 700 : 400,
                                                            fontSize: 13,
                                                            textDecoration: 'none',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                        onClick={() => handleNavClick(child.route)}
                                                        onMouseEnter={() => setHoveredItem(child.route)}
                                                        onMouseLeave={() => setHoveredItem(null)}
                                                    >
                                                        <span style={{ lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                                                            <child.icon size={15} strokeWidth={2} />
                                                        </span>
                                                        <span>{child.text}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* Regular item (no children) */
                                <div
                                    style={getItemStyle(item)}
                                    onClick={() => handleNavClick(item.route)}
                                    onMouseEnter={() => setHoveredItem(item.route)}
                                    onMouseLeave={() => setHoveredItem(null)}
                                    title={showLabels ? undefined : item.text}
                                >
                                    <span style={{ lineHeight: 1, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                                        <item.icon size={18} strokeWidth={2} />
                                    </span>
                                    {showLabels && (
                                        <span style={{ fontSize: 14, fontWeight: 'inherit' }}>
                                            {item.text}
                                        </span>
                                    )}
                                </div>
                            )}
                        </React.Fragment>
                        ))}

                    {/* sidebar footer: collapse toggle + logout */}
                    <div style={{
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        padding: showLabels ? '12px 12px' : '12px 8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        flexShrink: 0,

                    }}>
                        {/* logout button */}
                        <div
                            style={{
                                ...itemBase,
                                margin: 0,
                                color: 'rgba(255,255,255,0.6)',
                                fontSize: 14,
                            }}
                            onClick={handleLogout}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                        >
                            <span style={{ lineHeight: 1, flexShrink: 0, display: 'flex', alignItems: 'center' }}><LogOut size={18} strokeWidth={2} /></span>
                            {showLabels && <span>Log Out</span>}
                        </div>

                        {/* collapse / expand toggle -- desktop only */}
                        {!isMobile && !isTablet && (
                            <div
                                name="divExpandCollapse"
                                style={{
                                    ...itemBase,
                                    margin: 0,
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: 14,
                                }}
                                onClick={() => setExpanded(prev => !prev)}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                            >
                                <span style={{ lineHeight: 1, flexShrink: 0, display: 'flex', alignItems: 'center', transition: 'transform 0.25s', transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)' }}>
                                    <ChevronLeft size={18} strokeWidth={2} />
                                </span>
                                {showLabels && <span>Collapse</span>}
                            </div>
                        )}
                    </div>
                </nav>

                
            </aside>

            {/* -- RIGHT COLUMN ------------------------------------------------ */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                ...(isTablet ? { marginLeft: SIDEBAR_W_CLOSED } : {}),
            }}>
                {/* -- TOP BAR --------------------------------------------------- */}
                <header style={topBarStyle}>
                    {/* hamburger menu -- mobile only */}
                    {isMobile && (
                        <button
                            style={hamburgerStyle}
                            onClick={() => setSidebarOpen(prev => !prev)}
                            onMouseEnter={e => { e.currentTarget.style.background = '#e5e7eb'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                            aria-label="Toggle navigation menu"
                        >
                            <Menu size={20} strokeWidth={2} />
                        </button>
                    )}

                     {/*search -- hidden on mobile */}
                    {!isMobile && (
                        <div data-tour="global-search" style={{
                            flex: 1, maxWidth: 420, position: 'relative',
                        }}>
                            {/*<span style={{*/}
                            {/*    position: 'absolute', left: 12, top: '50%',*/}
                            {/*    transform: 'translateY(-50%)',*/}
                            {/*    color: '#9ca3af', pointerEvents: 'none',*/}
                            {/*    display: 'flex', alignItems: 'center',*/}
                            {/*}}>*/}
                            {/*    <Search size={16} strokeWidth={2} />*/}
                            {/*</span>*/}
                            {/*<input*/}
                            {/*    type="text"*/}
                            {/*    placeholder="Search claims, authorizations, members..."*/}
                            {/*    value={searchValue}*/}
                            {/*    onChange={e => setSearchValue(e.target.value)}*/}
                            {/*    style={{*/}
                            {/*        width: '100%', height: 40, borderRadius: 10,*/}
                            {/*        border: '1px solid #e5e7eb', paddingLeft: 40,*/}
                            {/*        paddingRight: 14, fontSize: 14, fontFamily: 'inherit',*/}
                            {/*        color: '#1f2937', background: '#f9fafb',*/}
                            {/*        outline: 'none', transition: 'border-color 0.2s',*/}
                            {/*    }}*/}
                            {/*    onFocus={e => { e.currentTarget.style.borderColor = COLOR.primary; }}*/}
                            {/*    onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}*/}
                            {/*/>*/}

                            <AutoComplete
                                    style={{
                                        width: '100%', height: 40,
                                        border: 'none', paddingLeft: 40,
                                        paddingRight: 0, paddingLeft:5, fontSize: 14, fontFamily: 'inherit',
                                        color: '#1f2937', background: '#f9fafb',
                                    outline: 'none', transition: 'border-color 0.2s',
                                        border:'none'
                                }}
                              //  className="global-search-autocomplete"
                                data={searchItems}
                                textField="text"
                                value={searchValue}
                                placeholder="&#x1F50E;&#xFE0E; Search claims, members, notifications..."
                                onChange={(e) => {
                                    setSearchValue(e.value);

                                    const item = searchItems.find(
                                        x => x.text.toLowerCase() === e.value.toLowerCase()
                                    );

                                    if (item) {
                                        navigate(item.path);
                                    }
                                }}
                            />
                        </div>
                    )}

                    {/* spacer */}
                    <div style={{ flex: 1 }} />


                    {/* help icon — opens Help & Support page */}
                    <div
                        name="divHelpAndSupport"
                        data-tour="help-icon"
                        onClick={() => navigate('/help')}
                        title="Help & Support"
                        style={{
                            width: 40, height: 40, borderRadius: 10,
                            background: location.pathname === '/help' ? '#e5e7eb' : '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', transition: 'background 0.2s',
                            color: '#374151', flexShrink: 0, marginRight: 8,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#e5e7eb'; }}
                        onMouseLeave={e => { if (location.pathname !== '/help') e.currentTarget.style.background = '#f3f4f6'; }}
                    >
                        <HelpCircle size={20} strokeWidth={2} />
                    </div>

                    {/* notification bell with dropdown */}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                        <div
                            name="divNotBell"
                            style={{
                                width: 40, height: 40, borderRadius: 10,
                                background: notifOpen ? '#e5e7eb' : '#f3f4f6', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', transition: 'background 0.2s',
                                position: 'relative', color: '#374151',
                            }}
                            onClick={() => setNotifOpen(!notifOpen)}
                            onMouseEnter={e => { e.currentTarget.style.background = '#e5e7eb'; }}
                            onMouseLeave={e => { if (!notifOpen) e.currentTarget.style.background = '#f3f4f6'; }}
                        >
                            <Bell size={20} strokeWidth={2} />
                            {notifs.filter(n => !n.read).length > 0 && (
                                <span style={{
                                    position: 'absolute', top: 4, right: 4,
                                    minWidth: 18, height: 18, borderRadius: 9,
                                    background: '#ef4444', border: '2px solid #ffffff',
                                    color: '#fff', fontSize: 10, fontWeight: 700,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    padding: '0 4px',
                                }}>
                                    {notifs.filter(n => !n.read).length}
                                </span>
                            )}
                        </div>

                        {/* Notification dropdown panel */}
                        {notifOpen && (
                            <>
                                {/* Backdrop */}
                                <div
                                    style={{ position: 'fixed', inset: 0, zIndex: 299 }}
                                    onClick={() => setNotifOpen(false)}
                                    />

                                    

                                    {/* Panel */}

                                <div style={{
                                    position: 'absolute', top: 48, zIndex: 9999,
                                    width: 380, maxHeight: 500,
                                    background: '#fff', borderRadius: 16,
                                    boxShadow: '0 12px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                                    overflow: 'hidden',
                                    animation: 'fadeInUp 0.2s ease-out',
                                    // desktop
                                    right: 0,
                                    left: 'auto',
                                    transform: 'none',

                                    // mobile (CENTER FIX)
                                    ...(isMobile
                                        ? {
                                            left: '100%',
                                            right: '100%',
                                            transform: 'translateX(-90%)',
                                            width: 'calc(100vw - 40px)',
                                        }
                                        : {}),                                   

                                }}>
                                    {/* Header */}
                                    <div style={{
                                        padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        borderBottom: '1px solid #E8ECF0',
                                    }}>
                                        <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1D21' }}>Notifications</div>
                                        {/*{notifs.some(n => !n.read) && (*/}
                                        {/*    <span*/}
                                        {/*        onClick={*/}
                                        {/*            () => setNotifs(notifs.map(n => ({ ...n, read: true })))*/}
                                        {/*        }*/}
                                        {/*        style={{ fontSize: 12, fontWeight: 600, color: COLOR.primary, cursor: 'pointer' }}*/}
                                        {/*    >*/}
                                        {/*        Mark all as read*/}
                                        {/*    </span>*/}
                                        {/*)}*/}

                                        {notifs.some(n => !n.read) && (
                                            <span
                                                onClick={markAllAsRead}
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: COLOR.primary,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Mark all as read
                                            </span>
                                        )}
                                    </div>

                                    {/* Notification list */}
                                        <div style={{ maxHeight: 400, overflowY: 'auto' }}>

                                            
                                            {notLoading ? (
                                                <div style={styles.gridOverlay}>
                                                    <LoadingSpinner
                                                        data-cy="notification-loader"
                                                        message="Loading Notifications..."
                                                    />
                                                </div>
                                            ) : notifs.length === 0 ? (
                                            <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
                                                <Bell size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
                                                <div style={{ fontWeight: 600 }}>No notifications</div>
                                            </div>
                                        ) : (
                                            notifs.map(n => {
                                                const typeIcon = n.type === 'claim' ? ClipboardList
                                                    : n.type === 'auth' ? Lock
                                                        : n.type === 'invoice' ? CreditCardIcon
                                                            : n.type === 'EOP' ? DollarSign
                                                                : n.type === 'ProviderAdminApproval' ? UserCheck
                                                                     : Wrench;
                                                const typeColor = n.type === 'claim' ? '#EF4444'
                                                    : n.type === 'auth' ? '#F59E0B'
                                                        : n.type === 'invoice' ? '#0D9488'
                                                            : n.type === 'ProviderAdminApproval' ? '#16A34A'
                                                                : n.type === 'EOP' ? '#F59E0B'
                                                                    : '#6366F1';
                                                const Icon = typeIcon;
                                                const timeAgo = (() => {
                                                    const diff = Date.now() - new Date(n.date).getTime();
                                                    const mins = Math.floor(diff / 60000);
                                                    if (mins < 60) return `${mins}m ago`;
                                                    const hrs = Math.floor(mins / 60);
                                                    if (hrs < 24) return `${hrs}h ago`;
                                                    const days = Math.floor(hrs / 24);
                                                    return `${days}d ago`;
                                                })();

                                                return (
                                                    <div
                                                        key={n.id}
                                                        onClick={() => {
                                                            setNotifs(notifs.map(x => x.id === n.id ? { ...x, read: true } : x));
                                                            
                                                            markNotificationAsRead([n.id]);
                                                        }}
                                                        style={{
                                                            padding: '14px 20px', cursor: 'pointer',
                                                            borderBottom: '1px solid #F1F5F9',
                                                            background: n.read ? 'transparent' : 'rgba(13,148,136,0.03)',
                                                            transition: 'background 0.15s',
                                                            display: 'flex', gap: 12, alignItems: 'flex-start',zIndex:9999
                                                        }}
                                                        onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFB'; }}
                                                        onMouseLeave={e => { e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(13,148,136,0.03)'; }}
                                                    >
                                                        {/* Icon */}
                                                        <div style={{
                                                            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                                                            background: `${typeColor}14`, display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center',
                                                        }}>
                                                            <Icon size={16} color={typeColor} strokeWidth={2} />
                                                        </div>
                                                        {/* Content */}
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{
                                                                fontSize: 13, fontWeight: n.read ? 500 : 700,
                                                                color: '#1A1D21', marginBottom: 2,
                                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                            }}>
                                                                {n.title}
                                                            </div>
                                                            <div style={{
                                                                fontSize: 12, color: '#6B7280', lineHeight: 1.4,
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: n.type === "ProviderAdminApproval" ? 7 : 2,
                                                                //WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',                                                                
                                                                overflow: 'hidden',
                                                            }}>
                                                                {n.message}
                                                                {n.type === "ProviderAdminApproval"
                                                                    ?                                                                    
                                                                        n.isApproved === "pending" ? (
                                                                        <>
                                                                            {accessPermissions.canApproveProviderAdmin && (
                                                                                <>
                                                                                    <button
                                                                                        name="btnApprove"
                                                                                        onClick={async () => await approveOrRejectUser(n.createdBy, true, n.others)}
                                                                                        title={`Approve ${n.others.providerName}`}
                                                                                        style={{
                                                                                            padding: '5px 10px',
                                                                                            borderRadius: 8,
                                                                                            border: 'none',
                                                                                            background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                                                                                            color: '#fff',
                                                                                            fontSize: 12,
                                                                                            fontWeight: 700,
                                                                                            cursor: 'pointer',
                                                                                            display: 'inline-flex',
                                                                                            alignItems: 'center',
                                                                                            gap: 4,
                                                                                            marginTop: '10px',
                                                                                            fontFamily: 'inherit',
                                                                                            boxShadow: '0 2px 6px rgba(139,92,246,0.3)',
                                                                                        }}
                                                                                    >
                                                                                        <UserCheck size={12} strokeWidth={2.5} />
                                                                                        Approve
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                                

                                                                            {accessPermissions.canRejectProviderAdmin && (
                                                                                <>
                                                                                    <button
                                                                                        name="btnReject"
                                                                                        onClick={async () => await approveOrRejectUser(n.createdBy, false, n.others)}
                                                                                        title={`Reject ${n.others.providerName}`}
                                                                                        style={{
                                                                                            padding: '5px 10px',
                                                                                            borderRadius: 8,
                                                                                            border: 'none',
                                                                                            marginLeft: '5px',
                                                                                            background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                                                                                            color: '#fff',
                                                                                            fontSize: 12,
                                                                                            fontWeight: 700,
                                                                                            cursor: 'pointer',
                                                                                            display: 'inline-flex',
                                                                                            alignItems: 'center',
                                                                                            gap: 4,
                                                                                            fontFamily: 'inherit',
                                                                                            boxShadow: '0 2px 6px rgba(139,92,246,0.3)',
                                                                                        }}
                                                                                    >
                                                                                        <UserX size={12} strokeWidth={2.5} />
                                                                                        Reject
                                                                                    </button>
                                                                                </>
                                                                            )}

                                                                                
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                {(n.approvedBy?.trim() || n.rejectedBy?.trim()) && (
                                                                                    <>
                                                                                        <br />
                                                                                        <strong>
                                                                                            {n.isApproved === "true" ? "Approved By:" : "Rejected By:"}
                                                                                        </strong>{" "}
                                                                                        {n.isApproved === "true" ? n.approvedBy : n.rejectedBy}
                                                                                    </>
                                                                                )}
                                                                            </>
                                                                        )
                                                                    
                                                                    :
                                                                    <>
                                                                        <a
                                                                            href="#"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                handleFileDownload(n.downloadUrl, n.checkNo, n.id);
                                                                            }}
                                                                        >
                                                                            {downloadingEop === n.id
                                                                                ? "Downloading..."
                                                                                : "Download"
                                                                            }

                                                                            <br></br>
                                                                        </a>
                                                                        {` File : ${n.downloadUrl}`}
                                                                    </>
                                                                }
                                                                
                                                            </div>
                                                            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>{timeAgo}</div>
                                                        </div>
                                                        {/* Unread dot */}
                                                        {!n.read && (
                                                            <div style={{
                                                                width: 8, height: 8, borderRadius: '50%',
                                                                background: COLOR.primary, flexShrink: 0, marginTop: 4,
                                                            }} />
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>

                                    {/* View all link */}
                                    <div
                                        onClick={() => { setNotifOpen(false); navigate('/notifications'); }}
                                        style={{
                                            padding: '12px 20px',
                                            textAlign: 'center',
                                            borderTop: '1px solid #E8ECF0',
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: COLOR.primary,
                                            cursor: 'pointer',
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFB'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        View all notifications →
                                    </div>
                                </div>
                            </>
                        )}
                    </div>


                    {/* user avatar */}
                    <div
                        name="divUserProfile"
                        style={{
                            display: 'flex', alignItems: 'center', gap: isMobile ? 0 : 10,
                            padding: isMobile ? '6px' : '6px 14px 6px 6px', borderRadius: 12,
                            cursor: 'pointer', transition: 'background 0.2s',
                            flexShrink: 0,
                        }}
                        onClick={() => navigate('/profile')}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: `linear-gradient(135deg, ${COLOR.primary}, ${COLOR.dark})`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: 13, fontWeight: 700,
                            letterSpacing: 0.5, flexShrink: 0,
                        }}>
                            {userInitials}
                        </div>
                        {/* name text -- hidden on mobile */}
                        {!isMobile && (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1f2937', lineHeight: 1.2 }}>
                                    {user ? `${user.firstName} ${user.lastName}` : 'User'}
                                </span>
                                <span style={{ fontSize: 11.5, color: '#6b7280', lineHeight: 1.2 }}>
                                    {user.userRole}
                                </span>
                            </div>
                        )}
                    </div>
                </header>

                {/* -- IMPERSONATION BANNER ------------------------------------- */}
                {isImpersonating && user && originalUser && (
                    <div style={{
                        background: 'linear-gradient(90deg, #FEF3C7, #FDE68A)',
                        borderBottom: '2px solid #F59E0B',
                        padding: isMobile ? '10px 14px' : '12px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                            <div style={{
                                width: 32, height: 32, borderRadius: 8,
                                background: '#F59E0B', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                                <Shield size={16} color="#fff" strokeWidth={2.5} />
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#92400E', marginBottom: 2 }}>
                                    Viewing as {user.firstName} {user.lastName} ({user.userRole})
                                </div>
                                <div style={{ fontSize: 11, color: '#B45309', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Super Admin session · Original user: {originalUser.firstName} {originalUser.lastName}
                                </div>
                            </div>
                        </div>
                        <button
                        name="btnEndImpersonate"
                            onClick={async () => {
                                await endImpersonation();
                                navigate('/admin/system-users');    
                                //await refreshNotifications();
                            }}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '8px 14px', borderRadius: 10,
                                border: '1.5px solid #DC2626', background: '#fff',
                                color: '#DC2626', fontWeight: 700, fontSize: 12,
                                cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
                            }}
                        >
                            End Impersonation
                        </button>
                    </div>
                )}

                {/* -- MAIN CONTENT ---------------------------------------------- */}
                <main style={mainContentStyle}>
                    <SocialProof route={location.pathname} />
                    <Outlet />
                </main>

                {/* -- FOOTER ---------------------------------------------------- */}
                <footer style={footerStyle}>
                    <span>&copy; 2026 Central Health Plans, Inc</span>
                    <span
                        name="spanTermsofService"
                        role="button"
                        tabIndex={0}
                        style={footerLinkStyle}
                        onClick={() => navigate('/terms')}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/terms'); }}
                        onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                    >
                        Terms of Service
                    </span>
                    <span
                        name="spanPrivacyPolicy"
                        role="button"
                        tabIndex={0}
                        style={footerLinkStyle}
                        onClick={() => navigate('/privacy')}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/privacy'); }}
                        onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                    >
                        Privacy Policy
                    </span>
                    <span
                        name="spanContactUs"
                        role="button"
                        tabIndex={0}
                        style={footerLinkStyle}
                        onClick={() => navigate('/contact-us')}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/contact-us'); }}
                        onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                    >
                        Contact Us
                    </span>
                    {feedbackCollect && (
                        <span
                            name="spanRateExperience"
                            role="button"
                            tabIndex={0}
                            style={footerLinkStyle}
                            onClick={() => window.dispatchEvent(new Event('sendero:open-feedback'))}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') window.dispatchEvent(new Event('sendero:open-feedback')); }}
                            onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                        >
                            Rate your experience
                        </span>
                    )}
                </footer>
            </div>

            {/* AI assistant (chat + self-service), first-run guided tour, feedback modal */}
            <ChatAssistant />
            <AppTour />
            <FeedbackWidget />

            {/* Session timeout warning modal */}
            <SessionTimeoutModal
                open={sessionTimeout.showWarning}
                secondsRemaining={sessionTimeout.secondsRemaining}
                onExtend={sessionTimeout.extendSession}
                onLogout={sessionTimeout.handleLogout}
            />
        </div>
        </>
    );
};
